import type {
  ActiInfo,
  ActivityItem,
  AnswerItem,
  PaperItem,
  monitorRoomItem
} from '@/api/activity/type'

export const MONITOR_SDK_APP_ID = 1600136974

export const ALL_MONITOR_POSITIONS = ['master', 'desk', 'slave'] as const
export const WEB_MONITOR_POSITIONS = ['master', 'desk'] as const

export type AllMonitorPosition = (typeof ALL_MONITOR_POSITIONS)[number]
export type MonitorPosition = (typeof WEB_MONITOR_POSITIONS)[number]
export type MonitorMode = 'none' | 'image-monitor' | 'session-monitor'

export type MonitorModePaper = Pick<
  PaperItem,
  'isOpenMasterMonitor' | 'isOpenDeskMonitor' | 'isOpenSlaveMonitor'
> & {
  ocIsImageMonitor?: boolean
}

export interface BaseMonitorPositionRoomConfig<TPosition extends string = AllMonitorPosition> {
  position: TPosition
  roomCode: string
  userId: string
}

const hasAnySessionMonitorFlag = (paper?: Partial<MonitorModePaper> | null) => {
  return !!paper?.isOpenMasterMonitor || !!paper?.isOpenDeskMonitor || !!paper?.isOpenSlaveMonitor
}

export const hasLiveVideoTrack = (stream?: MediaStream | null) => {
  const track = stream?.getVideoTracks?.()[0]
  return !!track && track.readyState === 'live'
}

export const hasLiveAudioTrack = (stream?: MediaStream | null) => {
  const track = stream?.getAudioTracks?.()[0]
  return !!track && track.readyState === 'live'
}

export type AllMonitorPositionRoomConfig = BaseMonitorPositionRoomConfig<AllMonitorPosition>
export type MonitorPositionRoomConfig = BaseMonitorPositionRoomConfig<MonitorPosition>

export interface MonitorRoomGroup {
  roomGroupId: number
  signature: string
  positions: Partial<Record<MonitorPosition, MonitorPositionRoomConfig>>
}

export interface SlaveMonitorQrPayload {
  actiId: number | string
  actiLinkId: number | string
  paperId: number | string
  accountId: number | string
  roomGroupId: number | string
  roomCode: string
  userId: string
  startDateTime: string
  key: string
}

const splitMonitorField = (value?: string) => {
  return (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

const encodeQrPayload = (payload: Record<string, any>) => {
  const payloadText = JSON.stringify(payload)
  const bytes = new TextEncoder().encode(payloadText)
  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

export const requiresSessionMonitor = (paper?: Partial<MonitorModePaper> | null) => {
  return !!paper?.isOpenMasterMonitor || !!paper?.isOpenDeskMonitor
}

export const getMonitorMode = (paper?: Partial<MonitorModePaper> | null): MonitorMode => {
  if (hasAnySessionMonitorFlag(paper)) {
    return 'session-monitor'
  }

  if (paper?.ocIsImageMonitor) {
    return 'image-monitor'
  }

  return 'none'
}

export const requiresMainCameraMonitor = (paper?: Partial<MonitorModePaper> | null) => {
  const monitorMode = getMonitorMode(paper)
  return monitorMode === 'image-monitor' || !!paper?.isOpenMasterMonitor
}

export const requiresPcAnswer = (paper?: Partial<MonitorModePaper> | null) => {
  return getMonitorMode(paper) !== 'none'
}

export const isWebMonitorEnabled = (paper?: Partial<MonitorModePaper> | null) => {
  return requiresSessionMonitor(paper)
}

export const buildMonitorPaperKey = ({
  actiLinkId,
  paperId,
  childrenPaperId
}: {
  actiLinkId: number | string
  paperId: number | string
  childrenPaperId?: number | string | null
}) => {
  return `${actiLinkId}_${paperId}_${childrenPaperId ?? 0}`
}

export const buildMonitorPaperKeyFromPaper = (
  paperItem: Pick<PaperItem, 'actiLinkId' | 'paperId' | 'answerList'>,
  childrenPaperItem?: Pick<AnswerItem, 'childrenPaperId'>
) => {
  return buildMonitorPaperKey({
    actiLinkId: paperItem.actiLinkId,
    paperId: paperItem.paperId,
    childrenPaperId:
      childrenPaperItem?.childrenPaperId ?? paperItem.answerList?.[0]?.childrenPaperId
  })
}

export const buildMonitorPaperKeyFromActiInfo = (actiInfo?: Partial<ActiInfo>) => {
  if (!actiInfo?.actiLinkId || !actiInfo?.paperId) return ''

  return buildMonitorPaperKey({
    actiLinkId: actiInfo.actiLinkId,
    paperId: actiInfo.paperId,
    childrenPaperId: actiInfo.actiInfoPaperList?.[0]?.childrenPaperId
  })
}

export const collectMonitoredPaperKeys = (activityItem?: ActivityItem) => {
  if (!activityItem) return []

  const keys = new Set<string>()

  activityItem.paperList.forEach((paperItem) => {
    if (!requiresSessionMonitor(paperItem)) return

    if (paperItem.paperType === 'B' && paperItem.groupFlag) {
      keys.add(buildMonitorPaperKeyFromPaper(paperItem))
      return
    }

    if (paperItem.answerList?.length) {
      paperItem.answerList.forEach((answerItem) => {
        keys.add(buildMonitorPaperKeyFromPaper(paperItem, answerItem))
      })
      return
    }

    keys.add(buildMonitorPaperKeyFromPaper(paperItem))
  })

  return Array.from(keys)
}

export const parseAllMonitorPositionConfigs = (monitorRoom?: monitorRoomItem) => {
  if (!monitorRoom) return {}

  const positions = splitMonitorField(monitorRoom.positions)
  const roomCodes = splitMonitorField(monitorRoom.roomCodes)
  const videoUserIds = splitMonitorField(monitorRoom.videoUserIds)
  const roomConfigMap: Partial<Record<AllMonitorPosition, AllMonitorPositionRoomConfig>> = {}

  positions.forEach((position, index) => {
    if (!ALL_MONITOR_POSITIONS.includes(position as AllMonitorPosition)) {
      return
    }

    const roomCode = roomCodes[index]
    const userId = videoUserIds[index]
    if (!roomCode || !userId) return

    const typedPosition = position as AllMonitorPosition
    roomConfigMap[typedPosition] = {
      position: typedPosition,
      roomCode,
      userId
    }
  })

  return roomConfigMap
}

export const getMonitorPositionConfig = (
  monitorRoom: monitorRoomItem | undefined,
  position: AllMonitorPosition
) => {
  return parseAllMonitorPositionConfigs(monitorRoom)[position]
}

export const parseMonitorRoomGroup = (monitorRoom?: monitorRoomItem): MonitorRoomGroup | null => {
  if (!monitorRoom) return null

  const roomConfigMap: MonitorRoomGroup['positions'] = {}
  const allPositionConfigs = parseAllMonitorPositionConfigs(monitorRoom)

  WEB_MONITOR_POSITIONS.forEach((position) => {
    const roomConfig = allPositionConfigs[position]
    if (!roomConfig) return

    roomConfigMap[position] = {
      position,
      roomCode: roomConfig.roomCode,
      userId: roomConfig.userId
    }
  })

  const signature = WEB_MONITOR_POSITIONS.filter((position) => roomConfigMap[position])
    .map((position) => {
      const roomConfig = roomConfigMap[position]!
      return `${position}:${roomConfig.roomCode}:${roomConfig.userId}`
    })
    .join('|')

  if (!signature) {
    return null
  }

  return {
    roomGroupId: monitorRoom.roomGroupId,
    signature,
    positions: roomConfigMap
  }
}

export const buildSlaveMonitorQrValue = (payload: SlaveMonitorQrPayload) => {
  return `normstar://monitor/slave?payload=${encodeURIComponent(encodeQrPayload(payload))}`
}
